import React, { useState, useImperativeHandle, useEffect } from "react";
import { UploadedDocument } from "./UploadedDocument";
import { DocumentType } from "@/shared/types/documentTypes";
import useDocumentUpload, {
  DocumentFormInput,
  StagedDocument,
  UploadResult,
  StoreDocumentResponse,
} from "@/shared/hooks/useDocumentUpload";
import DocumentUploadInput from "./DocumentUploadInput";
import { cn } from "@/shared/utils/cn";
import { useAppContext } from "@/shared/hooks/useAppContext";
import ParseWillDialog from "@/shared/components/document-viewer/ParseWillDialog";
import pluralize from "@/shared/utils/pluralize";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/shared/components/ui/dialog";
import { UseFormRegister } from "react-hook-form";
import { toast } from "@/shared/components/ui/use-toast";

import { useDocumentLimitPaywall } from "@/shared/hooks/useDocumentLimitPaywall";

interface UploadDocumentsFormProps {
  onCancel?: () => void;
  onSuccess: (
    uploadedDocuments?: UploadResult[] | StoreDocumentResponse[] | StagedDocument[],
  ) => void;
  documentTypes: { id: DocumentType; label: string }[];
  className?: string;
  multiple?: boolean;
  initiallyShowButtons?: boolean;
  displayUploadingInDialog?: boolean;
  disableParsingAnimation?: boolean;
  // Action Center integration props
  postLossTaskId?: number;
  stepLabel?: string;
  autoSaveCallback?: (documentIds: number[]) => void;
  existingDocumentIds?: number[];
}

interface DocumentContentProps {
  documents: StagedDocument[];
  documentTypes: { id: DocumentType; label: string }[];
  documentTypeFields: { id: string; documentType: DocumentType }[];
  register: UseFormRegister<DocumentFormInput>;
  handleDocumentTypeChange: (e: DocumentType, ix: number) => void;
  handleDocumentRemove: (ix: number) => void;
  handleDocumentTypeRemove: (ix: number) => void;
  errorIndexes: number[];
}

export const UploadDocumentsForm = React.forwardRef<
  {
    submitForm: () => void;
    isSubmitting: boolean;
    stageRemoteDocument: (remote: {
      fileKey: string;
      fileName: string;
      contentType?: string;
      documentType?: DocumentType;
    }) => void;
    getStagedDocuments: () => StagedDocument[];
    handleDocumentTypeChange: (v: DocumentType, ix: number) => void;
    handleDocumentRemove: (ix: number) => void;
    stagedDocumentsCount: number;
    errorIndexes: number[];
  },
  UploadDocumentsFormProps
>(
  (
    {
      onSuccess,
      onCancel,
      documentTypes,
      className,
      multiple = true,
      initiallyShowButtons = true,
      displayUploadingInDialog = false,
      disableParsingAnimation = false,
      postLossTaskId,
      stepLabel,
      autoSaveCallback,
      existingDocumentIds = [],
    },
    ref,
  ) => {
    const { user, person } = useAppContext();
    const [showUploadingDialog, setShowUploadingDialog] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { show: showLimitPaywall, paywall: limitPaywall } = useDocumentLimitPaywall();
    const notify = toast;
    const {
      handleSubmit,
      onSubmit,
      register,
      handleDocumentAdd,
      stageRemoteDocument,
      uploading,
      failed,
      succeeded,
      documents,
      handleDocumentTypeChange,
      handleDocumentRemove,
      documentTypeFields,
      handleDocumentTypeRemove,
      submitDisabled,
      parsing,
      setParsing,
      errorIndexes,
      clearDocuments,
      hitDocumentLimit,
    } = useDocumentUpload({
      onComplete: (uploadResults) => {
        setIsSubmitting(false);
        setShowUploadingDialog(false);

        const uploadedDocuments =
          (uploadResults?.length ? uploadResults : null) ??
          (succeeded.length ? succeeded : null) ??
          documents;

        onSuccess(uploadedDocuments);
      },
      onFirstDocumentAdded: () => {
        if (displayUploadingInDialog) {
          setShowUploadingDialog(true);
        }
      },
      deceasedUserId: user?.last_active_estate_id || person?.estate_id || undefined,
      postLossTaskId,
      documentTypes,
      disableParsingAnimation,
      stepLabel,
      autoSaveCallback,
      existingDocumentIds,
    });

    useEffect(() => {
      if (succeeded.length > 0 && !uploading) {
        const successCount = succeeded.length;
        notify({
          title: `${successCount} ${pluralize("document", successCount)} uploaded successfully!`,
        });
      }
    }, [succeeded, uploading, notify]);

    useEffect(() => {
      if (failed.length > 0 && !uploading) {
        failed.forEach((error) => {
          notify({
            title: `Error: ${error}`,
            variant: "destructive",
          });
        });
      }
    }, [failed, uploading, notify]);

    useEffect(() => {
      if (hitDocumentLimit) showLimitPaywall();
    }, [hitDocumentLimit, showLimitPaywall]);

    // Auto-close upload dialog when upload completes and no parsing is needed
    useEffect(() => {
      if (showUploadingDialog && !uploading && succeeded.length > 0 && !parsing) {
        // Upload completed successfully and no parsing needed, close dialog immediately
        setShowUploadingDialog(false);
      }
    }, [showUploadingDialog, uploading, succeeded, parsing]);

    const showButtons = initiallyShowButtons || documents.length > 0;
    const handleFormSubmit = () => {
      setIsSubmitting(true);
      handleSubmit(onSubmit)();
    };

    useImperativeHandle(ref, () => ({
      submitForm: handleFormSubmit,
      isSubmitting,
      stageRemoteDocument,
      getStagedDocuments: () => documents,
      handleDocumentTypeChange,
      handleDocumentRemove,
      stagedDocumentsCount: documents.length,
      errorIndexes,
    }));

    return (
      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleFormSubmit();
        }}
        className={cn(["flex flex-col w-full", className])}
      >
        <div className="gap-6 flex flex-col w-full">
          <DocumentUploadInput
            onChange={handleDocumentAdd}
            multiple={multiple}
            disabled={documentTypes.length > 0 && documents.length >= documentTypes.length}
            disabledMessage={`Maximum of ${documentTypes.length} ${pluralize(
              "document",
              documentTypes.length,
            )} allowed`}
            className="w-full"
          />
          {/* TODO: add this back when ready to integrate
          <div className="flex flex-col w-full h-27 bg-transparent">
            <p className="mb-4">Import From</p>
            <div className="flex flex-col md:grid md:grid-cols-2 lg:flex lg:flex-row gap-4 w-full">
              <Button
                variant="outline"
                className="flex-1 h-20 flex items-center gap-2"
                onClick={() => {
                  const fileInput = document.getElementById(
                    'upload-input',
                  ) as HTMLInputElement;
                  if (fileInput) {
                    fileInput.click();
                  }
                }}
              >
                <Monitor className="w-5 h-5" />
                Your Device
              </Button>
              <Button
                variant="outline"
                className="flex-1 h-20 flex items-center gap-2"
                onClick={() => {
                  trackEvent(EventType.FEATURE_COMING_SOON, {
                    feature: 'Import From Google Drive',
                  });
                  enqueueSnackbar(TOAST.COMING_SOON, {
                    variant: 'info',
                  });
                }}
              >
                <img src={driveSvg} alt="Google Drive" className="w-5 h-5" />
                Google Drive
              </Button>
              <Button
                variant="outline"
                className="flex-1 h-20 flex items-center gap-2"
                onClick={() => {
                  trackEvent(EventType.FEATURE_COMING_SOON, {
                    feature: 'Import From Dropbox',
                  });
                  enqueueSnackbar(TOAST.COMING_SOON, {
                    variant: 'info',
                  });
                }}
              >
                <img src={dropboxSvg} alt="Dropbox" className="w-5 h-5" />
                Dropbox
              </Button>
              <Button
                variant="outline"
                className="flex-1 h-20 flex items-center gap-2"
                onClick={() => {
                  trackEvent(EventType.FEATURE_COMING_SOON, {
                    feature: 'Import From OneDrive',
                  });
                  enqueueSnackbar(TOAST.COMING_SOON, {
                    variant: 'info',
                  });
                }}
              >
                <img src={onedriveSvg} alt="OneDrive" className="w-5 h-5" />
                OneDrive
              </Button>
            </div>
          </div> */}
          {!displayUploadingInDialog && (
            <DocumentContent
              documents={documents}
              documentTypes={documentTypes}
              documentTypeFields={documentTypeFields}
              register={register}
              handleDocumentTypeChange={handleDocumentTypeChange}
              handleDocumentRemove={handleDocumentRemove}
              handleDocumentTypeRemove={handleDocumentTypeRemove}
              errorIndexes={errorIndexes}
            />
          )}
        </div>

        <ParseWillDialog
          isOpen={parsing}
          onComplete={() => {
            setParsing(false);
            onSuccess();
          }}
        />

        <UploadingDialog
          open={showUploadingDialog}
          documents={documents}
          documentTypes={documentTypes}
          documentTypeFields={documentTypeFields}
          register={register}
          handleDocumentTypeChange={handleDocumentTypeChange}
          handleDocumentRemove={handleDocumentRemove}
          handleDocumentTypeRemove={handleDocumentTypeRemove}
          errorIndexes={errorIndexes}
          showButtons={showButtons}
          onCancel={onCancel}
          submitDisabled={submitDisabled}
          onSubmit={handleFormSubmit}
          setOpen={(val: boolean) => {
            if (!val) {
              clearDocuments();
            }
            setShowUploadingDialog(val);
          }}
        />
        {limitPaywall}
      </form>
    );
  },
);

function DocumentContent({
  documents,
  documentTypes,
  documentTypeFields,
  register,
  handleDocumentTypeChange,
  handleDocumentRemove,
  handleDocumentTypeRemove,
  errorIndexes,
}: DocumentContentProps) {
  return (
    <div className="flex flex-col flex-grow max-h-[500px] gap-4 overflow-auto">
      {documents.map((document, ix) => (
        <UploadedDocument
          documentTypes={documentTypes}
          key={documentTypeFields[ix].id}
          document={document}
          documentType={document.documentType}
          register={register}
          ix={ix}
          onDocumentTypeChange={(e: DocumentType) => {
            handleDocumentTypeChange(e, ix);
          }}
          onDocumentRemove={() => {
            handleDocumentRemove(ix);
            handleDocumentTypeRemove(ix);
          }}
          isErrored={errorIndexes.includes(ix)}
        />
      ))}
    </div>
  );
}

interface UploadingDialogProps extends DocumentContentProps {
  open: boolean;
  setOpen: (val: boolean) => void;
  showButtons?: boolean;
  onCancel?: () => void;
  submitDisabled?: boolean;
  onSubmit?: () => void;
}

function UploadingDialog({
  open,
  documents,
  documentTypes,
  documentTypeFields,
  register,
  handleDocumentTypeChange,
  handleDocumentRemove,
  handleDocumentTypeRemove,
  errorIndexes,
  setOpen,
}: UploadingDialogProps) {
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="md:w-[900px] md:h-[80%] max-w-none">
        <DialogHeader>
          <DialogTitle>Upload Progress</DialogTitle>
        </DialogHeader>
        <div className="flex-grow overflow-hidden">
          <DocumentContent
            documents={documents}
            documentTypes={documentTypes}
            documentTypeFields={documentTypeFields}
            register={register}
            handleDocumentTypeChange={handleDocumentTypeChange}
            handleDocumentRemove={handleDocumentRemove}
            handleDocumentTypeRemove={handleDocumentTypeRemove}
            errorIndexes={errorIndexes}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
